import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import { useCompany } from '../../contexts/CompanyContext';

const CompanySwitcher = () => {
  const { currentCompany, userCompanies, switchCompany, loading } = useCompany();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if no companies
  if (!userCompanies || userCompanies.length === 0) {
    return null;
  }

  // If only one company, show it but without dropdown functionality
  const hasMultipleCompanies = userCompanies.length > 1;

  const handleSwitch = async (companyId) => {
    if (companyId !== currentCompany?.id) {
      await switchCompany(companyId);
    }
    setIsOpen(false);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'primary_owner':
      case 'owner':
        return 'bg-purple-500 bg-opacity-20 text-purple-400';
      case 'executive':
      case 'general_manager':
        return 'bg-blue-500 bg-opacity-20 text-blue-400';
      case 'department_head':
      case 'manager':
        return 'bg-green-500 bg-opacity-20 text-green-400';
      case 'supervisor':
      case 'staff':
        return 'bg-surface text-text-secondary';
      case 'contractor':
        return 'bg-orange-500 bg-opacity-20 text-orange-400';
      default:
        return 'bg-surface text-text-secondary';
    }
  };

  const formatRole = (role) => {
    return role?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => hasMultipleCompanies && setIsOpen(!isOpen)}
        disabled={loading}
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg 'bg-background border-surface' ${hasMultipleCompanies ? 'hover:bg-surface cursor-pointer' : 'cursor-default'
          } transition-colors`}
      >
        <div className="w-8 h-8 bg-orange-500 bg-opacity-20 rounded-lg flex items-center justify-center overflow-hidden">
          {currentCompany?.logo_url ? (
            <img
              src={currentCompany.logo_url}
              alt={currentCompany.company_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <Icon name="Building2" size={16} color="#f97316" />
          )}
        </div>
        <div className="text-left hidden sm:block">
          <p className="text-text-primary text-sm font-medium truncate max-w-[120px]">
            {currentCompany?.company_name || 'Select Company'}
          </p>
          <p className="text-xs text-text-secondary">
            {formatRole(currentCompany?.role)}
          </p>
        </div>
        {hasMultipleCompanies && (
          <Icon
            name="ChevronDown"
            size={16}
            className={`text-text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {/* Dropdown - only for multiple companies */}
      {isOpen && hasMultipleCompanies && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-background border border-border rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="p-2 border-b border-border">
            <p className="text-xs text-text-secondary uppercase tracking-wider px-2">Switch Company</p>
          </div>

          <div className="max-h-64 overflow-y-auto py-2">
            {userCompanies.map((company) => {
              const isSelected = company.company_id === currentCompany?.id;
              return (
                <button
                  key={company.company_id}
                  onClick={() => handleSwitch(company.company_id)}
                  className={`w-full px-3 py-3 flex items-center space-x-3 transition-colors ${isSelected
                    ? 'bg-orange-500 bg-opacity-10 border-l-2 border-orange-500'
                    : 'hover:bg-surface'
                    }`}
                >
                  <div className="w-10 h-10 bg-border rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {/* Show logo for current company if selected, otherwise show icon */}
                    {isSelected && currentCompany?.logo_url ? (
                      <img
                        src={currentCompany.logo_url}
                        alt={company.company_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Icon name="Building2" size={18} color={isSelected ? '#f97316' : '#9ca3af'} />
                    )}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className={`font-medium truncate ${isSelected ? 'text-orange-400' : 'text-text-primary'}`}>
                      {company.company_name}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${getRoleBadgeColor(company.role)}`}>
                        {formatRole(company.role)}
                      </span>
                      <span className="text-xs text-text-secondary">
                        @{company.company_tag}
                      </span>
                    </div>
                  </div>
                  {isSelected && (
                    <Icon name="Check" size={18} color="#f97316" className="flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySwitcher;
